import React, { useEffect } from 'react';
import { AlertCircle } from 'lucide-react';

/**
 * Centered confirmation modal (warning icon, message, Cancel / confirm).
 * @param {Object} props
 * @param {boolean} props.open
 * @param {React.ReactNode} props.message
 * @param {string} [props.confirmLabel]
 * @param {boolean} [props.danger] Red confirm button.
 * @param {boolean} [props.busy]
 * @param {Array<{label: string, onClick: () => void}>} [props.extraActions] Additional buttons between Cancel and confirm.
 * @param {React.ReactNode} [props.children] Extra body content (e.g. radio options).
 * @param {() => void} props.onCancel
 * @param {() => void} props.onConfirm
 * @returns {JSX.Element|null}
 */
export default function ConfirmDialog({
  open, message, confirmLabel = 'OK', danger = false, busy = false, extraActions = [], children, onCancel, onConfirm,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-xs p-4" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div role="alertdialog" aria-modal="true" data-testid="confirm-dialog" className="w-full max-w-md rounded-xl bg-civix-panel border border-civix-border shadow-2xl p-6 space-y-5">
        <AlertCircle className="w-14 h-14 mx-auto text-amber-300/80" strokeWidth={1.25} />
        <div className="text-sm text-white/80 leading-relaxed">{message}</div>
        {children}
        <div className="flex flex-wrap justify-end gap-2">
          <button type="button" onClick={onCancel} className="px-4 py-2 rounded-md text-sm font-semibold bg-civix-panelLight text-white/80 hover:text-white">Cancel</button>
          {extraActions.map((a) => (
            <button key={a.label} type="button" onClick={a.onClick} disabled={busy} className="px-4 py-2 rounded-md text-sm font-semibold bg-civix-panelLight text-civix-purpleSoft hover:text-white disabled:opacity-50">{a.label}</button>
          ))}
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            data-testid="confirm-ok"
            className={`px-5 py-2 rounded-md text-sm font-bold text-white disabled:opacity-50 ${danger ? 'bg-red-500 hover:bg-red-400' : 'bg-civix-purple hover:bg-civix-purpleDeep'}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
